import api from '@shared/services/api';
import { MediaItem } from '../../spaces/types/space.types';
import {
  CreateResourceData,
  Resource,
  ResourceMaterial,
  ResourceResponse,
  ResourceSortType,
  ResourcesResponse,
  ReviewResourceData,
  UpdateResourceData,
} from '../types/resource.types';

/**
 * Service for interacting with the Resources API
 */
export const ResourceService = {
  /**
   * Get resources with pagination and sorting
   */
  async getResources(
    page: number = 1,
    limit: number = 10,
    sortType: ResourceSortType = ResourceSortType.NEWEST
  ): Promise<ResourcesResponse> {
    const response = await api.get<ResourcesResponse>('/resources', {
      params: { page, limit, sortType },
    });
    return response.data;
  },

  /**
   * Get a specific resource by ID
   */
  async getResource(resourceId: string): Promise<Resource | null> {
    try {
      const response = await api.get<Resource>(`/resources/${resourceId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching resource ${resourceId}:`, error);
      return null;
    }
  },

  /**
   * Create a new resource
   */
  async createResource(data: CreateResourceData): Promise<Resource | null> {
    try {
      const response = await api.post<ResourceResponse>('/resources', data);
      return response.data.resource;
    } catch (error) {
      console.error('Error creating resource:', error);
      return null;
    }
  },

  /**
   * Update an existing resource
   */
  async updateResource(
    resourceId: string,
    data: UpdateResourceData
  ): Promise<Resource | null> {
    try {
      // Get the current resource to compare with updated data
      const currentResource = await this.getResource(resourceId);
      if (!currentResource) {
        throw new Error('Resource not found');
      }

      // Track removed materials and media items for proper cleanup
      const removedMaterialIds = this.findRemovedMaterialIds(currentResource.materials, data.materials || []);
      const removedMediaItems = this.findRemovedMediaItems(
        currentResource.featuredImage || [],
        data.featuredImage || [],
        currentResource.materials,
        data.materials || []
      );

      // Add metadata about removed items to the update request
      const enhancedData = {
        ...data,
        _metadata: {
          removedMaterialIds,
          removedMediaItems
        }
      };

      const response = await api.put<ResourceResponse>(`/resources/${resourceId}`, enhancedData);
      return response.data.resource;
    } catch (error) {
      console.error(`Error updating resource ${resourceId}:`, error);
      return null;
    }
  },

  /**
   * Find material IDs that were removed in the update
   */
  findRemovedMaterialIds(
    currentMaterials: ResourceMaterial[],
    updatedMaterials: Partial<ResourceMaterial>[]
  ): string[] {
    // Filter out materials that don't have IDs (new materials)
    const currentIds = currentMaterials
      .filter(material => material.id)
      .map(material => material.id as string);

    const updatedIds = updatedMaterials
      .filter(material => material.id)
      .map(material => material.id as string);

    // Return IDs that exist in current but not in updated
    return currentIds.filter(id => !updatedIds.includes(id));
  },

  /**
   * Find media items that were removed in the update
   */
  findRemovedMediaItems(
    currentFeaturedImage: MediaItem[],
    updatedFeaturedImage: MediaItem[],
    currentMaterials: ResourceMaterial[],
    updatedMaterials: Partial<ResourceMaterial>[]
  ): MediaItem[] {
    const removedItems: MediaItem[] = [];

    // Check if featured image was removed
    if (currentFeaturedImage.length > 0 && updatedFeaturedImage.length === 0) {
      removedItems.push(...currentFeaturedImage);
    }

    // Create a map of updated materials by ID for easy lookup
    const updatedMaterialsMap = new Map();
    updatedMaterials.forEach(material => {
      if (material.id) {
        updatedMaterialsMap.set(material.id, material);
      }
    });

    // Check each current material
    currentMaterials.forEach(currentMaterial => {
      if (!currentMaterial.id) return;

      const updatedMaterial = updatedMaterialsMap.get(currentMaterial.id);

      // If material was completely removed, add all its media items
      if (!updatedMaterial) {
        if (currentMaterial.image && currentMaterial.image.length > 0) {
          removedItems.push(...currentMaterial.image);
        }
        if (currentMaterial.resourceFile && currentMaterial.resourceFile.length > 0) {
          removedItems.push(...currentMaterial.resourceFile);
        }
        return;
      }

      // Check if image was removed
      if (currentMaterial.image && currentMaterial.image.length > 0 &&
        (!updatedMaterial.image || updatedMaterial.image.length === 0)) {
        removedItems.push(...currentMaterial.image);
      }

      // Check if resource file was removed
      if (currentMaterial.resourceFile && currentMaterial.resourceFile.length > 0 &&
        (!updatedMaterial.resourceFile || updatedMaterial.resourceFile.length === 0)) {
        removedItems.push(...currentMaterial.resourceFile);
      }
    });

    return removedItems;
  },

  /**
   * Delete a resource
   */
  async deleteResource(resourceId: string): Promise<boolean> {
    try {
      const response = await api.delete<{ success: boolean }>(`/resources/${resourceId}`);
      return response.data.success;
    } catch (error) {
      console.error(`Error deleting resource ${resourceId}:`, error);
      return false;
    }
  },

  /**
   * Search resources
   */
  async searchResources(
    query: string,
    page: number = 1,
    limit: number = 10
  ): Promise<ResourcesResponse> {
    try {
      const response = await api.get<ResourcesResponse>('/resources/search', {
        params: { query, page, limit },
      });
      return response.data;
    } catch (error) {
      console.error('Error searching resources:', error);
      throw error;
    }
  },

  /**
   * Get all resources for admin management (all statuses)
   */
  async getAdminResources(page: number = 1, limit: number = 20): Promise<ResourcesResponse> {
    const response = await api.get<ResourcesResponse>('/resources/management', {
      params: { page, limit },
    });
    return response.data;
  },

  /**
   * Get resources created by the current user
   */
  async getMyResources(page: number = 1, limit: number = 20): Promise<ResourcesResponse> {
    const response = await api.get<ResourcesResponse>('/resources/my-resources', {
      params: { page, limit },
    });
    return response.data;
  },

  /**
   * Approve or reject a resource (admin only)
   */
  async reviewResource(resourceId: string, data: ReviewResourceData): Promise<Resource | null> {
    try {
      const response = await api.put<ResourceResponse>(`/resources/management/${resourceId}/review`, data);
      return response.data.resource;
    } catch (error) {
      console.error(`Error reviewing resource ${resourceId}:`, error);
      return null;
    }
  },
};
